import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { EditOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import ListPage from '@components/common/layout/ListPage';
import DragDropTableV2 from '@components/common/table/DragDropTableV2';
import { DEFAULT_TABLE_ITEM_SIZE } from '@constants';
import apiConfig from '@constants/apiConfig';
import useDragDropV2 from '@hooks/useDragDropV2';
import useListBase from '@hooks/useListBase';
import useTranslate from '@hooks/useTranslate';
import { BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { orderNumber } from '@utils';
import { Button } from 'antd';

import useKitplayAreaFormModal from './KitplayAreaFormModal';

import styles from '../index.module.scss';

const message = defineMessages({
    objectName: 'Area',
});

const GroupArea = ({ serverDetail }) => {
    const translate = useTranslate();
    const [editingRowId, setEditingRowId] = useState(null);

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const tabKey = params.get('tab');
    const restaurantId = params.get('restaurantId');
    const restaurantName = params.get('restaurantName');

    const { data, mixinFuncs, loading, pagination, pagePath, changePagination } = useListBase({
        apiConfig: apiConfig.areas,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(message.objectName),
        },
        params: {
            restaurantId,
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.getList = () => {
                mixinFuncs.handleFetchList({
                    restaurantId,
                });
            };
            funcs.getCreateLink = () => {
                return `${pagePath}/kitplay/area/create?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&deviceId=${serverDetail?.id}&ordering=${data?.length ? data.length : 0}`;
            };
            funcs.getItemDetailLink = (dataRow) => {
                return `${pagePath}/kitplay/area/${dataRow.id}?tab=${tabKey}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&deviceId=${serverDetail?.id}`;
            };
            funcs.additionalActionColumnButtons = () => ({
                editArea: (record) => {
                    if (!mixinFuncs.hasPermission([apiConfig.areas.update.baseURL])) return null;
                    return (
                        <BaseTooltip type="edit">
                            <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingRowId(record.id);
                                    openEdit(record);
                                }}
                            >
                                <EditOutlined />
                            </Button>
                        </BaseTooltip>
                    );
                },
            });
        },
    });

    const { openCreate, openEdit, renderModals } = useKitplayAreaFormModal({
        restaurantId,
        deviceId: serverDetail?.id,
        onReload: () => {
            mixinFuncs.getList(),
            setEditingRowId(null);
        },
        onClose: () => setEditingRowId(null),
    });
    

    const {
        sortedData,
        sortColumn,
        onDragEndDnd,
        loadingOrdering,
    } = useDragDropV2({
        data: data || [],
        apiConfig: apiConfig.areas.updateSort,
        indexField: 'ordering',
        onUpdated: () => {
            mixinFuncs.getList();
        },
    });

    const columns = [
        sortColumn,
        {
            title: '#',
            dataIndex: 'index',
            key: 'id',
            render: (text, record, index) => {
                return orderNumber(pagination, index);
            },
            width: 50,
        },
        {
            title: <FormattedMessage defaultMessage="Name" />,
            dataIndex: 'name',
            key: 'name',
        },
        mixinFuncs.renderActionColumn(
            {
                editArea: mixinFuncs.hasPermission([apiConfig.areas.update.baseURL]),
                delete: mixinFuncs.hasPermission([apiConfig.areas.delete.baseURL]),
            },
            { width: '120px' },
        ),
    ].filter(Boolean);

    const renderActionBar = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', gap: 12 }}>
                {mixinFuncs.hasPermission([apiConfig.areas.create.baseURL]) && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openCreate(data ? data.length : 0)}
                    >
                    </Button>
                )}
                <BaseTooltip title={translate.formatMessage(commonMessage.refresh)}>
                    <Button
                        type="default"
                        onClick={() => {
                            mixinFuncs.getList();
                        }}  
                        loading={loading}
                        icon={<SyncOutlined />}
                    >
                    </Button>
                </BaseTooltip>
            </div >
        );
    };

    return (
        <>
            <ListPage
                actionBar={renderActionBar()}
                baseTable={
                    <DragDropTableV2
                        scroll={{ }}
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={sortedData}
                        onDragEnd={onDragEndDnd}
                        loading={loading || loadingOrdering}
                        rowKey={(record) => record.id}
                        pagination={pagination}
                        rowClassName={(record) => {
                            return record.id === editingRowId ? styles['highlighted-row'] : '';
                        }}
                    />
                }
            />
            {renderModals()}
        </>
    );
};

export default GroupArea;
